<template>
  <transition :name="`${prefixClass}-zoom-in-down`">
    <div
      v-if="visible"
      :class="`${prefixClass}-datepicker-main ${prefixClass}-datepicker-popup`"
      :style="{ top, left, position: 'absolute' }">
      <slot/>
    </div>
  </transition>
</template>

<script>
import { getScrollParent } from 'toolkit/utils/date-format-parse/util.js'

const rafThrottle = (fn) => {
  let isRunning = false
  return function fnBinfRaf(...args) {
    if (isRunning) { return }
    isRunning = true
    requestAnimationFrame(() => {
      isRunning = false
      fn.apply(this, args)
    })
  }
}

const getPopupElementSize = (element) => {
  const originalDisplay = element.style.display
  const originalVisibility = element.style.visibility
  element.style.display = 'block'
  element.style.visibility = 'hidden'
  const styles = window.getComputedStyle(element)
  const width =
    element.offsetWidth + parseInt(styles.marginLeft, 10) + parseInt(styles.marginRight, 10)
  const height =
    element.offsetHeight + parseInt(styles.marginTop, 10) + parseInt(styles.marginBottom, 10)
  element.style.display = originalDisplay
  element.style.visibility = originalVisibility
  return { width, height }
}

/**
 * get the popup position
 * @param {HTMLElement} el relative element
 * @param {Number} targetWidth target element's width
 * @param {Number} targetHeight target element's height
 * @param {Boolean} fixed
 */
const getRelativePosition = (el, targetWidth, targetHeight, fixed) => {
  let left = 0
  let top = 0
  let offsetX = 0
  let offsetY = 0
  const relativeRect = el.getBoundingClientRect()
  const dw = document.documentElement.clientWidth
  const dh = document.documentElement.clientHeight
  if (fixed) {
    offsetX = window.pageXOffset + relativeRect.left
    offsetY = window.pageYOffset + relativeRect.top
  }
  if (dw - relativeRect.left < targetWidth && relativeRect.right < targetWidth) {
    left = offsetX - relativeRect.left + 1
  } else if (relativeRect.left + relativeRect.width / 2 <= dw / 2) {
    left = offsetX
  } else {
    left = offsetX + relativeRect.width - targetWidth
  }
  if (relativeRect.top <= targetHeight && dh - relativeRect.bottom <= targetHeight) {
    top = offsetY + dh - relativeRect.top - targetHeight
  } else if (relativeRect.top + relativeRect.height / 2 <= dh / 2) {
    top = offsetY + relativeRect.height
  } else {
    top = offsetY - targetHeight
  }
  return { left: `${left}px`, top: `${top}px` }
}



export default {
  name: 'DatePopup',
  inject: {
    prefixClass: {
      default: 's-datepicker',
    },
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    appendToBody: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      top: '',
      left: '',
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler(val) {
        this.$nextTick(() => {
          if (val) {
            this.displayPopup()
          }
        })
      },
    },
  },
  mounted() {
    if (this.appendToBody) {
      document.body.appendChild(this.$el)
    }
    this._clickoutEvent = 'ontouchend' in document ? 'touchstart' : 'mousedown'

    document.addEventListener(this._clickoutEvent, this.handleClickOutside)

    // change the popup position when resize or scroll
    const relativeElement = this.$parent.$el
    this._displayPopup = rafThrottle(() => this.displayPopup())
    this._scrollParent = getScrollParent(relativeElement) || window
    this._scrollParent.addEventListener('scroll', this._displayPopup)
    window.addEventListener('resize', this._displayPopup)
  },
  beforeDestroy() {
    if (this.appendToBody && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el)
    }

    document.removeEventListener(this._clickoutEvent, this.handleClickOutside)

    this._scrollParent.removeEventListener('scroll', this._displayPopup)
    window.removeEventListener('resize', this._displayPopup)
  },
  methods: {
    handleClickOutside(evt) {
      if (!this.visible) { return }
      const { target } = evt
      const el = this.$el
      if (el && !el.contains(target)) {
        this.$emit('clickoutside', evt)
      }
    },
    displayPopup() {
      if (!this.visible) { return }
      const popup = this.$el
      const relativeElement = this.$parent.$el
      const { appendToBody } = this
      if (!this._popupRect) {
        this._popupRect = getPopupElementSize(popup)
      }
      const { width, height } = this._popupRect
      const { left, top } = getRelativePosition(relativeElement, width, height, appendToBody)
      this.left = left
      this.top = top
    },
  },
}
</script>